import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { pickLocalizedValue } from "../i18n/I18nContext";
import { formatPropertyPrice } from "../Utils/propertyPricing";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  EASTERN_CITY_CENTERS,
  normalizeCityKey,
  resolvePropertyMapPoint,
} from "../Utils/mapCoordinates";

const FALLBACK_IMAGE = "/img/property-placeholder.svg";

function buildGlowIcon(active = false) {
  return L.divIcon({
    className: `qtech-map-pin${active ? " is-active" : ""}`,
    html: `
      <span class="qtech-map-pin__pulse"></span>
      <span class="qtech-map-pin__core"></span>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -12],
  });
}

export default function PropertyMapExplorer({
  properties = [],
  loading = false,
  locale = "en",
  isRtl = false,
  resolveImage = (url) => url || FALLBACK_IMAGE,
  labels = {},
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const markerByIdRef = useRef(new Map());
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedId, setSelectedId] = useState("");

  const copy = {
    eyebrow: labels.eyebrow || (locale === "ar" ? "خريطة العقارات" : "Property Map"),
    title: labels.title || (locale === "ar" ? "عقاراتنا في المنطقة الشرقية" : "Our properties in Eastern Province"),
    subtitle:
      labels.subtitle ||
      (locale === "ar"
        ? "اختر المدينة، اضغط على الدبوس، وانتقل مباشرة إلى العقار."
        : "Filter by city, tap a pin, and open the property."),
    all: labels.all || (locale === "ar" ? "الكل" : "All"),
    empty: labels.empty || (locale === "ar" ? "لا توجد عقارات على الخريطة حالياً." : "No mapped properties yet."),
    loading: labels.loading || (locale === "ar" ? "جاري تحميل الخريطة..." : "Loading map..."),
    viewProperty: labels.viewProperty || (locale === "ar" ? "عرض العقار" : "View property"),
    approx: labels.approx || (locale === "ar" ? "موقع تقريبي حسب المدينة" : "Approx. city location"),
    countLabel: labels.countLabel || (locale === "ar" ? "عقارات" : "properties"),
  };

  const mappedProperties = useMemo(() => {
    return (Array.isArray(properties) ? properties : [])
      .map((property) => {
        const point = resolvePropertyMapPoint(property);
        if (!point) return null;
        return {
          ...property,
          __map: point,
          __cityKey: point.cityKey || normalizeCityKey(property.city || property.area || ""),
        };
      })
      .filter(Boolean);
  }, [properties]);

  const cityFilters = useMemo(() => {
    const counts = new Map();
    mappedProperties.forEach((property) => {
      const key = property.__cityKey;
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    const preferred = Object.keys(EASTERN_CITY_CENTERS).filter((city) => counts.has(city));
    const extras = [...counts.keys()].filter((city) => !preferred.includes(city)).sort();
    return [...preferred, ...extras].map((city) => ({
      key: city,
      count: counts.get(city) || 0,
    }));
  }, [mappedProperties]);

  const visibleProperties = useMemo(() => {
    if (selectedCity === "all") return mappedProperties;
    return mappedProperties.filter((property) => property.__cityKey === selectedCity);
  }, [mappedProperties, selectedCity]);

  const selectedProperty = useMemo(
    () => visibleProperties.find((property) => property.id === selectedId) || null,
    [visibleProperties, selectedId]
  );

  useEffect(() => {
    if (!selectedId) return;
    if (!visibleProperties.some((property) => property.id === selectedId)) {
      setSelectedId("");
    }
  }, [selectedId, visibleProperties]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return undefined;

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
    }).setView([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng], DEFAULT_MAP_ZOOM);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: isRtl ? "topleft" : "topright" }).addTo(map);

    const markersLayer = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;
    markersLayerRef.current = markersLayer;

    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      map.remove();
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
      markerByIdRef.current = new Map();
    };
  }, [isRtl]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const markersLayer = markersLayerRef.current;
    if (!map || !markersLayer) return;

    markersLayer.clearLayers();
    markerByIdRef.current = new Map();

    visibleProperties.forEach((property) => {
      const { lat, lng } = property.__map;
      const marker = L.marker([lat, lng], {
        icon: buildGlowIcon(false),
        riseOnHover: true,
      });

      marker.on("click", () => {
        setSelectedId(property.id);
      });

      marker.addTo(markersLayer);
      markerByIdRef.current.set(property.id, marker);
    });

    if (visibleProperties.length === 0) {
      if (selectedCity !== "all" && EASTERN_CITY_CENTERS[selectedCity]) {
        const center = EASTERN_CITY_CENTERS[selectedCity];
        map.flyTo([center.lat, center.lng], 11, { duration: 0.8 });
      } else {
        map.flyTo([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng], DEFAULT_MAP_ZOOM, { duration: 0.8 });
      }
      return;
    }

    const bounds = L.latLngBounds(visibleProperties.map((property) => [property.__map.lat, property.__map.lng]));
    map.flyToBounds(bounds.pad(0.25), {
      maxZoom: visibleProperties.length === 1 ? 14 : 12,
      duration: 0.85,
    });
  }, [visibleProperties, selectedCity]);

  useEffect(() => {
    markerByIdRef.current.forEach((marker, id) => {
      marker.setIcon(buildGlowIcon(id === selectedId));
      if (id === selectedId) {
        try {
          marker.setZIndexOffset(1000);
        } catch {
          /* ignore */
        }
      } else {
        try {
          marker.setZIndexOffset(0);
        } catch {
          /* ignore */
        }
      }
    });
  }, [selectedId]);

  const selectedTitle = selectedProperty
    ? pickLocalizedValue(selectedProperty, "title", locale, copy.viewProperty)
    : "";
  const selectedImage = selectedProperty
    ? resolveImage(selectedProperty.coverImage || selectedProperty.image || "")
    : FALLBACK_IMAGE;
  const selectedPrice = selectedProperty
    ? formatPropertyPrice(selectedProperty, {
        locale,
        negotiationText: locale === "ar" ? "اضغط للتفاوض" : "Click for negotiation",
      })
    : null;
  const selectedPlace = selectedProperty
    ? [selectedProperty.area, selectedProperty.city].filter(Boolean).join(", ")
    : "";

  return (
    <section className="qtech-map-explorer" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container">
        <div className="qtech-map-explorer__intro text-center">
          <h6 className="qtech-map-explorer__eyebrow">{copy.eyebrow}</h6>
          <h2 className="qtech-map-explorer__title">{copy.title}</h2>
          <p className="qtech-map-explorer__subtitle">{copy.subtitle}</p>
        </div>

        <div className="qtech-map-explorer__filters" role="tablist" aria-label={copy.eyebrow}>
          <button
            type="button"
            className={`qtech-map-chip${selectedCity === "all" ? " is-active" : ""}`}
            onClick={() => setSelectedCity("all")}
          >
            {copy.all}
            <span>{mappedProperties.length}</span>
          </button>
          {cityFilters.map((city) => (
            <button
              key={city.key}
              type="button"
              className={`qtech-map-chip${selectedCity === city.key ? " is-active" : ""}`}
              onClick={() => setSelectedCity(city.key)}
            >
              {city.key}
              <span>{city.count}</span>
            </button>
          ))}
        </div>

        <div className="qtech-map-explorer__stage">
          <div className="qtech-map-explorer__canvas-wrap">
            <div ref={mapRef} className="qtech-map-explorer__canvas" />
            {loading ? <div className="qtech-map-explorer__overlay">{copy.loading}</div> : null}
            {!loading && visibleProperties.length === 0 ? (
              <div className="qtech-map-explorer__overlay">{copy.empty}</div>
            ) : null}
          </div>

          {selectedProperty ? (
            <aside className="qtech-map-explorer__card">
              <button
                type="button"
                className="qtech-map-explorer__card-close"
                aria-label="Close"
                onClick={() => setSelectedId("")}
              >
                ×
              </button>
              <div className="qtech-map-explorer__card-media">
                <img src={selectedImage} alt={selectedTitle} />
              </div>
              <div className="qtech-map-explorer__card-body">
                <p className="qtech-map-explorer__card-place">{selectedPlace}</p>
                <h3>{selectedTitle}</h3>
                {selectedPrice?.text ? <p className="qtech-map-explorer__card-price">{selectedPrice.text}</p> : null}
                {!selectedProperty.__map.precise ? (
                  <p className="qtech-map-explorer__card-note">{copy.approx}</p>
                ) : null}
                <Link to={`/property/${selectedProperty.id}`} className="qtech-map-explorer__card-cta">
                  {copy.viewProperty}
                </Link>
              </div>
            </aside>
          ) : (
            <aside className="qtech-map-explorer__hint">
              <strong>
                {visibleProperties.length} {copy.countLabel}
              </strong>
              <span>{copy.subtitle}</span>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
